import React, { createContext, useContext, useState, useEffect } from 'react';
import {
  browserLocalPersistence,
  onAuthStateChanged,
  setPersistence,
  signInWithCustomToken,
  signInWithEmailAndPassword,
  signOut,
  updatePassword as updateFirebasePassword,
  reauthenticateWithCredential,
  EmailAuthProvider,
  User,
} from 'firebase/auth';
import { Credentials, FirebaseProviderProps, IFirebaseContext } from './interface';
import { localizeError } from './utils';

declare global {
  interface Window {
    Cypress: unknown;
  }
}

const TEST_USER: Partial<User> = { email: 'test@example.com' };

export const FirebaseContext = createContext<IFirebaseContext>({
  googleUser: null,
  loading: true,
  login: undefined,
  logout: undefined,
  updatePassword: undefined,
});

export const FirebaseProvider = ({ children, auth }: FirebaseProviderProps): React.ReactElement => {
  const [googleUser, setGoogleUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const url = new URL(window.location.href);
  const searchParams = new URLSearchParams(url.search);
  const impersonateToken = searchParams.get('impersonateToken');

  useEffect(() => {
    const impersonateOneUser = async () => {
      if (impersonateToken) {
        await signOut(auth);
        signInWithCustomToken(auth, impersonateToken);
        window.localStorage.setItem('impersonating', 'true');
      }
    };

    impersonateOneUser();
  }, [impersonateToken]);

  onAuthStateChanged(auth, async (user) => {
    if (user) {
      setGoogleUser(user);
    }
    setLoading(false);
  });

  const login = async (credentials: Credentials) => {
    try {
      await setPersistence(auth, browserLocalPersistence);
      await signInWithEmailAndPassword(auth, credentials.email, credentials.password);
    } catch (error: any) {
      const localizedError = localizeError(error);
      throw localizedError;
    }
  };

  const updatePassword = async (password: string) => {
    try {
      if (!!googleUser) {
        await updateFirebasePassword(googleUser, password);
      }
    } catch (error) {
      throw error;
    }
  };

  const signInWithToken = async (token: string) => {
    await signInWithCustomToken(auth, token);
  };

  const reauthenticateUser = async (password: string) => {
    try {
      if (!!googleUser && googleUser.email) {
        const credential = EmailAuthProvider.credential(googleUser.email, password);
        await reauthenticateWithCredential(googleUser, credential);
      }
    } catch (error: any) {
      const localizedError = localizeError(error);
      throw localizedError;
    }
  };

  const logout = async (redirectUrl?: string) => {
    await signOut(auth);
    window.localStorage.removeItem('impersonating');
    if (redirectUrl) {
      window.location.replace(redirectUrl);
    } else {
      window.location.assign('/');
    }
  };

  return (
    <FirebaseContext.Provider
      value={{
        googleUser: !!window.Cypress ? TEST_USER : googleUser,
        loading,
        login,
        logout,
        updatePassword,
        reauthenticateUser,
        signInWithToken,
      }}
    >
      {children}
    </FirebaseContext.Provider>
  );
};

export const useFirebaseContext = (): IFirebaseContext => useContext(FirebaseContext);
